import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';
import { Link, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useTranslation } from '../../hooks/translations/TranslationProvider';
import useSeo from '../../hooks/useSeo';
import { useAppSelector } from '../../store/hooks';
import LandingFilters from './LandingFilters';
import { Button } from '../common';
import './landing.css';

const Landing = () => {
    const navigate = useLocalizedNavigate();
    const { lang } = useParams();
    const { t } = useTranslation();
    const locale = lang === 'pl' ? 'pl' : 'en';
    const auth = useAppSelector(state => state.auth);

    useSeo({ title: t('seo.landing.title'), description: t('seo.landing.description'), path: '/' });
    useEffect(() => {
        if (auth.authData) navigate('/home');
    }, [auth.authData, navigate]);

    const cities = locale === 'pl'
        ? ['Warszawa', 'Lublin']
        : ['London', 'Manchester'];
    const cityUrl = (city: string) => `/${locale}/finder?${new URLSearchParams({ country: locale === 'pl' ? 'PL' : 'GB', city })}`;

    return (
        <div className="landing-page">
            <section className="landing-hero" aria-labelledby="landing-title">
                <div className="landing-hero-copy">
                    <span className="landing-eyebrow">{t('landing.eyebrow')}</span>
                    <h1 id="landing-title">{t('landing.title')}</h1>
                    <p className="landing-lead">{t('landing.subtitle')}</p>
                    <div className="landing-search-panel">
                        <h2>{t('landing.searchTitle')}</h2>
                        <LandingFilters />
                        <div className="landing-shortcuts">
                            <span>{t('landing.quickCities')}</span>
                            {cities.map(city => <Link key={city} to={cityUrl(city)}>{city}</Link>)}
                        </div>
                    </div>
                    <p className="landing-search-note">{t('landing.browseNote')}</p>
                </div>
                <div className="landing-hero-photo">
                    <div className="landing-photo-caption">
                        <span className="landing-eyebrow">{t('landing.photoEyebrow')}</span>
                        <h2>{t('landing.photoTitle')}</h2>
                        <p>{t('landing.photoText')}</p>
                    </div>
                </div>
            </section>

            <section className="landing-section landing-how" aria-labelledby="landing-how-title">
                <span className="landing-eyebrow">{t('landing.howItWorks')}</span>
                <h2 id="landing-how-title">{t('landing.howItWorksSub')}</h2>
                <div className="landing-step-grid">
                    {['browseEvents', 'joinAndConnect', 'playAndEnjoy'].map((step, index) => <article key={step}>
                        <span className="landing-step-number">0{index + 1}</span>
                        <h3>{t(`landing.${step}`)}</h3>
                        <p>{t(`landing.${step}Sub`)}</p>
                    </article>)}
                </div>
            </section>

            <section className="landing-organizer">
                <div><span className="landing-eyebrow">{t('landing.organizerEyebrow')}</span><h2>{t('landing.organizerTitle')}</h2><p>{t('landing.organizerText')}</p></div>
                <Button
                    text={t('landing.organizerAction')}
                    variant="white"
                    size="md"
                    onClick={() => navigate(`/${locale}/register`)}
                />
            </section>

            <section className="landing-section landing-faq-section" aria-labelledby="landing-faq-title">
                <div><span className="landing-eyebrow">{t('landing.faqEyebrow')}</span><h2 id="landing-faq-title">{t('landing.faqTitle')}</h2></div>
                <div className="landing-faq-list">{[1, 2, 3].map(number => <details key={number}>
                    <summary>{t(`landing.faq${number}Question`)}</summary><p>{t(`landing.faq${number}Answer`)}</p>
                </details>)}</div>
            </section>
            <section className="landing-final">
                <h2>{t('landing.ctaTitle')}</h2><p>{t('landing.ctaSub')}</p>
                <Button
                    text={t('landing.searchGames')}
                    variant="green"
                    size="md"
                    onClick={() => {
                        document.getElementById('landing-title')?.scrollIntoView();
                        document.getElementById('city')?.focus({ preventScroll: true });
                    }}
                />
            </section>
        </div>
    );
};
export default Landing;
